import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Lock, Mail, Building2, Phone, KeyRound, ArrowLeft, Check, Monitor, Download, Zap } from 'lucide-react';

import { API } from '../utils/api';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(null);
  const msgRef = useRef(null);

  const LOADING_MESSAGES = [
    '🔐 Kimlik doğrulanıyor...',
    '🛡️ Güvenlik katmanları kontrol ediliyor...',
    '🔒 Şifreleme algoritması çalışıyor...',
    '✅ Hesabınıza erişim sağlanıyor...',
    '⚡ Neredeyse bitti...',
  ];

  useEffect(() => {
    if (loading) {
      setLoadingMsg(LOADING_MESSAGES[0]);
      setProgress(5);
      let msgIdx = 0;
      let prog = 5;

      progressRef.current = setInterval(() => {
        prog = Math.min(prog + Math.random() * 8 + 2, 88);
        setProgress(prog);
      }, 400);

      msgRef.current = setInterval(() => {
        msgIdx = (msgIdx + 1) % LOADING_MESSAGES.length;
        setLoadingMsg(LOADING_MESSAGES[msgIdx]);
      }, 2200);
    } else {
      setProgress(100);
      setTimeout(() => setProgress(0), 400);
      clearInterval(progressRef.current);
      clearInterval(msgRef.current);
    }
    return () => {
      clearInterval(progressRef.current);
      clearInterval(msgRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  const navigate = useNavigate();
  
  const [view, setView] = useState('main'); // 'main', 'verify_email', 'forgot_password', 'reset_password', 'download_app'
  const isElectron = navigator.userAgent.toLowerCase().includes('electron');

  const saveAuthData = (data) => {
    if (rememberMe) {
      localStorage.setItem('admin_token', data.token);
    } else {
      sessionStorage.setItem('admin_token', data.token);
    }
    localStorage.setItem('admin_email', data.email);
    localStorage.setItem('tenant_id', data.tenant_id);
    if (data.business_name) localStorage.setItem('business_name', data.business_name);
    if (data.name) localStorage.setItem('user_name', data.name);
    localStorage.setItem('user_type', data.user_type);
    if (data.role) localStorage.setItem('user_role', data.role);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!isElectron) {
       toast.error("Giriş yapmak için Windows uygulamasını indirmelisiniz.");
       setView('download_app');
       return;
    }
    setLoading(true);
    
    // Önce işletme sahibi olarak dene
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      saveAuthData(response.data);
      toast.success('Giriş başarılı!');
      navigate('/admin/dashboard');
      return;
    } catch (ownerError) {
      // E-posta doğrulama gerekiyorsa direkt göster
      if (ownerError.response?.status === 403 && ownerError.response?.data?.detail?.includes('doğrulayın')) {
        toast.error(ownerError.response.data.detail);
        setView('verify_email');
        setLoading(false);
        return;
      }
      // 401 değilse (sunucu hatası vs.) çalışan denemesi yapma
      if (ownerError.response?.status !== 401) {
        toast.error('Giriş sırasında bir hata oluştu. Lütfen tekrar deneyin.');
        setLoading(false);
        return;
      }
    }

    // İşletme sahibi değilse çalışan olarak dene
    try {
      const empResponse = await axios.post(`${API}/auth/employee/login`, { email, password });
      saveAuthData(empResponse.data);
      toast.success(`Hoş geldin ${empResponse.data.name}!`);
      
      const perms = empResponse.data.permissions || [];
      if (perms.includes('all') || perms.includes('view_dashboard') || perms.includes('revenue')) {
        navigate('/admin/dashboard');
      } else if (perms.includes('orders') || perms.includes('view_orders')) {
        navigate('/admin/orders');
      } else if (perms.includes('tables') || perms.includes('view_tables')) {
        navigate('/admin/tables');
      } else {
        // Fallback to dashboard (will show empty/unauthorized if they truly have no perms, but better than nothing)
        navigate('/admin/dashboard');
      }
    } catch (empError) {
      toast.error('E-posta veya şifre hatalı.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (isElectron) {
      toast.error('Kayıt işlemi sadece websitesinden yapılabilir.');
      return;
    }
    if (!businessName || !email || !password || !phone) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, {
        business_name: businessName,
        email,
        phone,
        password
      });
      toast.success(response.data.message || 'Doğrulama kodu gönderildi!');
      setView('verify_email');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Kayıt başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (!code) {
      toast.error('Lütfen doğrulama kodunu girin');
      return;
    }
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { email, code });
      toast.success("Hesabınız başarıyla onaylandı!");
      if (!isElectron) {
         setView('download_app');
      } else {
         setView('main');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Doğrulama başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Lütfen e-posta adresinizi girin');
      return;
    }
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success(response.data.message);
      setView('reset_password');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!code || !newPassword) {
      toast.error('Lütfen kod ve yeni şifrenizi girin');
      return;
    }
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/reset-password`, { email, code, new_password: newPassword });
      toast.success(response.data.message);
      setView('main');
      setPassword('');
      setCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Şifre sıfırlama başarısız');
    } finally {
      setLoading(false);
    }
  };

  /* ─── Shared field styles ─── */
  const inputCls =
    'pl-10 w-full rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/25 focus:border-orange-500 focus:ring-0 focus:outline-none transition-colors py-2.5 text-sm';
  const labelCls = 'block text-sm text-white/50 mb-1.5';
  const iconCls = 'absolute left-3 top-1/2 -translate-y-1/2 text-white/40';

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ backgroundColor: '#07070B' }}
    >
      {/* ── LOADING OVERLAY ── */}
      {loading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center"
          style={{ backgroundColor: 'rgba(7,7,11,0.97)', backdropFilter: 'blur(12px)' }}>
          {/* Logo */}
          <img src="/logo.png" alt="Cularix" className="w-14 h-14 object-contain mb-6 drop-shadow-2xl" 
            style={{ animation: 'pulse 2s infinite' }} />

          {/* Spinner halkası */}
          <div className="w-16 h-16 rounded-full border-4 border-white/5 border-t-orange-500 mb-8"
            style={{ animation: 'spin 1s linear infinite' }} />

          {/* Mesaj */}
          <p className="text-white font-semibold text-lg mb-2 transition-all duration-500">
            {loadingMsg}
          </p>
          <p className="text-white/30 text-sm mb-8">Lütfen bekleyin, bu işlem 10-30 saniye sürebilir.</p>

          {/* Progress bar */}
          <div className="w-64 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%`, backgroundColor: '#f97316', boxShadow: '0 0 12px rgba(249,115,22,0.6)' }}
            />
          </div>
        </div>
      )}

      {/* Ambient orange glow – top-left */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 10% 10%, rgba(249,115,22,0.18) 0%, transparent 70%)',
        }}
      />
      {/* Ambient orange glow – bottom-right */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 50% 40% at 90% 90%, rgba(249,115,22,0.10) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 w-full max-w-md px-6">
        {/* ── Glass card ── */}
        <div
          className="rounded-3xl p-8"
          style={{
            background: 'rgba(255,255,255,0.04)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 32px 80px rgba(0,0,0,0.5)',
          }}
        >
          {/* ── Logo area ── */}
          <div className="flex flex-col items-center mb-8">
            <img src="/logo.png" alt="Cularix" className="w-16 h-auto object-contain mb-4 drop-shadow-2xl" />
            <h1 className="text-2xl font-bold text-white tracking-tight">Cularix POS</h1>
            <p className="text-white/40 text-sm mt-1">Güvenli Yönetim Sistemi</p>
          </div>

          {/* ══════════════════════════════
              VIEW: main
          ══════════════════════════════ */}
          {view === 'main' && (
            <Tabs defaultValue={isElectron ? 'login' : 'register'} className="w-full">
              <TabsList
                className="grid w-full grid-cols-1 mb-6 rounded-xl p-1"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                {isElectron ? (
                  <TabsTrigger
                    value="login"
                    className="rounded-lg text-white/50 data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-sm font-semibold"
                  >
                    Giriş Yap
                  </TabsTrigger>
                ) : (
                  <TabsTrigger
                    value="register"
                    className="rounded-lg text-white/50 data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-sm font-semibold"
                  >
                    Sisteme Kayıt Ol
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Login tab */}
              {isElectron && (
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <label className={labelCls}>E-Posta Adresi</label>
                      <div className="relative">
                        <Mail className={iconCls} size={18} />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className={inputCls}
                          placeholder="ornek@sirket.com"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>Şifre</label>
                      <div className="relative">
                        <Lock className={iconCls} size={18} />
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className={inputCls}
                          placeholder="••••••••"
                          required
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <label className="flex items-center cursor-pointer text-white/40 hover:text-white/60 transition-colors select-none">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="mr-2 rounded border-white/20 bg-white/5 text-orange-500 focus:ring-orange-500"
                        />
                        Beni Hatırla
                      </label>
                      <button
                        type="button"
                        onClick={() => setView('forgot_password')}
                        className="text-orange-400 hover:text-orange-300 transition-colors"
                      >
                        Şifremi Unuttum
                      </button>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-white rounded-xl font-semibold py-3 transition-all shadow-lg mt-2"
                      style={{ boxShadow: '0 8px 24px rgba(249,115,22,0.25)' }}
                    >
                      {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                    </button>
                  </form>
                </TabsContent>
              )}

              {/* Register tab */}
              {!isElectron && (
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <label className={labelCls}>İşletme Adı</label>
                      <div className="relative">
                        <Building2 className={iconCls} size={18} />
                        <input
                          type="text"
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          className={inputCls}
                          placeholder="Restoranım"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>E-Posta Adresi</label>
                      <div className="relative">
                        <Mail className={iconCls} size={18} />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className={inputCls}
                          placeholder="ornek@sirket.com"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>Telefon Numarası</label>
                      <div className="relative">
                        <Phone className={iconCls} size={18} />
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className={inputCls}
                          placeholder="0555 555 55 55"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>Şifre</label>
                      <div className="relative">
                        <Lock className={iconCls} size={18} />
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className={inputCls}
                          placeholder="••••••••"
                          required
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-white rounded-xl font-semibold py-3 transition-all shadow-lg mt-2"
                      style={{ boxShadow: '0 8px 24px rgba(249,115,22,0.25)' }}
                    >
                      {loading ? 'İşlem yapılıyor...' : 'Devam Et'}
                    </button>
                  </form>
                </TabsContent>
              )}
            </Tabs>
          )}

          {/* ══════════════════════════════
              VIEW: download_app
          ══════════════════════════════ */}
          {view === 'download_app' && (
            <div className="text-center space-y-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
                style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.25)' }}
              >
                <Check size={30} className="text-green-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Harika!</h2>
                <p className="text-white/50 text-sm leading-relaxed">
                  Sisteme başarıyla kayıt oldunuz. İşletmenizi yönetmeye başlamak için şimdi masaüstü uygulamasını
                  indirmelisiniz.
                </p>
              </div>

              <a
                href="https://github.com/kabilberke0/cularix_frontend/releases/download/v0.0.2/Cularix.POS.Setup.0.0.2.exe"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center w-full py-4 px-5 rounded-2xl group relative overflow-hidden transition-all"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.10)',
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/15 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0"
                  style={{ background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.25)' }}
                >
                  <Monitor size={20} className="text-orange-400" />
                </div>
                <div className="flex flex-col items-start text-left flex-1">
                  <span className="font-semibold text-white text-sm leading-tight">Windows Uygulamasını İndir</span>
                  <span className="text-xs text-white/35 mt-0.5">Sürüm 0.0.2 • Güvenli Kurulum</span>
                </div>
                <Download
                  size={18}
                  className="text-white/30 group-hover:text-orange-400 group-hover:translate-y-0.5 transition-all ml-3"
                />
              </a>

              <p className="text-xs text-white/25">
                İndirdikten sonra uygulamayı kurup e-posta ve şifrenizle giriş yapabilirsiniz.
              </p>
            </div>
          )}

          {/* ══════════════════════════════
              VIEW: verify_email
          ══════════════════════════════ */}
          {view === 'verify_email' && (
            <form onSubmit={handleVerifyEmail} className="space-y-5">
              <p className="text-white/50 text-sm text-center leading-relaxed">
                E-posta adresinize (<span className="text-orange-400">{email}</span>) 6 haneli bir doğrulama kodu
                gönderildi. Lütfen kodunuzu girin.
              </p>

              <div>
                <label className={labelCls}>Doğrulama Kodu</label>
                <div className="relative">
                  <KeyRound className={iconCls} size={18} />
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className={`${inputCls} text-center tracking-[0.5em] font-mono text-lg`}
                    placeholder="123456"
                    maxLength={6}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-white rounded-xl font-semibold py-3 transition-all shadow-lg"
                style={{ boxShadow: '0 8px 24px rgba(249,115,22,0.25)' }}
              >
                {loading ? 'Doğrulanıyor...' : 'Doğrula ve Başla'}
              </button>

              <button
                type="button"
                onClick={() => setView('main')}
                className="flex items-center justify-center w-full text-sm text-white/40 hover:text-white/70 transition-colors"
              >
                <ArrowLeft size={15} className="mr-1.5" /> Geri Dön
              </button>
            </form>
          )}

          {/* ══════════════════════════════
              VIEW: forgot_password
          ══════════════════════════════ */}
          {view === 'forgot_password' && (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <p className="text-white/50 text-sm text-center leading-relaxed">
                Sisteme kayıtlı e-posta adresinizi girin. Size bir şifre sıfırlama kodu göndereceğiz.
              </p>

              <div>
                <label className={labelCls}>E-Posta Adresi</label>
                <div className="relative">
                  <Mail className={iconCls} size={18} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputCls}
                    placeholder="ornek@sirket.com"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-white rounded-xl font-semibold py-3 transition-all shadow-lg"
                style={{ boxShadow: '0 8px 24px rgba(249,115,22,0.25)' }}
              >
                {loading ? 'Gönderiliyor...' : 'Şifre Sıfırlama Kodu Gönder'}
              </button>

              <button
                type="button"
                onClick={() => setView('main')}
                className="flex items-center justify-center w-full text-sm text-white/40 hover:text-white/70 transition-colors"
              >
                <ArrowLeft size={15} className="mr-1.5" /> Geri Dön
              </button>
            </form>
          )}

          {/* ══════════════════════════════
              VIEW: reset_password
          ══════════════════════════════ */}
          {view === 'reset_password' && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <p className="text-white/50 text-sm text-center leading-relaxed">
                E-posta adresinize gönderilen kodu ve yeni şifrenizi girin.
              </p>

              <div>
                <label className={labelCls}>Sıfırlama Kodu</label>
                <div className="relative">
                  <KeyRound className={iconCls} size={18} />
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className={`${inputCls} text-center tracking-[0.5em] font-mono text-lg`}
                    placeholder="123456"
                    maxLength={6}
                    required
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>Yeni Şifre</label>
                <div className="relative">
                  <Lock className={iconCls} size={18} />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={inputCls}
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-white rounded-xl font-semibold py-3 transition-all shadow-lg"
                style={{ boxShadow: '0 8px 24px rgba(249,115,22,0.25)' }}
              >
                {loading ? 'Sıfırlanıyor...' : 'Şifreyi Güncelle'}
              </button>

              <button
                type="button"
                onClick={() => setView('main')}
                className="flex items-center justify-center w-full text-sm text-white/40 hover:text-white/70 transition-colors"
              >
                <ArrowLeft size={15} className="mr-1.5" /> Geri Dön
              </button>
            </form>
          )}
        </div>

        {/* Footer version */}
        <p className="text-white/20 text-xs text-center mt-6">Cularix POS v0.0.2</p>
      </div>
    </div>
  );
};

export default LoginPage;